import InvoiceStateBadge from '@/components/invoices/InvoiceStateBadge'
import MissingEmailModal from '@/components/recaps/MissingEmailModal'
import MonthNavigation from '@/components/recaps/MonthNavigation'
import { useRecapActions } from '@/hooks/useRecapActions'
import { Contract, ContractRecapSummary, Invoice, SharedProps } from '@/types'
import { formatCurrency } from '@/utils/format'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { ActionIcon, Button, Group, Paper, Stack, Table, Text, TextInput, Title, Tooltip } from '@mantine/core'
import { IconDownload, IconFileInvoice, IconSearch, IconSend, IconX } from '@tabler/icons-react'
import { useState } from 'react'

interface RecapsIndexPageProps extends SharedProps {
  contracts: Contract[]
  summaries: Record<number, ContractRecapSummary>
  month: string
  invoices: Record<number, Invoice[]>
  e_invoicing_connected: boolean
}

function ContractRow({
  contract,
  summary,
  invoices,
  showInvoices,
  isSendingEmail,
  onSelect,
  onDownloadPdf,
  onSendEmail,
}: {
  contract: Contract
  summary: ContractRecapSummary | undefined
  invoices: Invoice[]
  showInvoices: boolean
  isSendingEmail: boolean
  onSelect: (contract: Contract) => void
  onDownloadPdf: (contract: Contract) => void
  onSendEmail: (contract: Contract) => void
}) {
  const hasOrders = (summary?.orders_count ?? 0) > 0

  const handleClick = () => onSelect(contract)

  // The row itself opens the recap, so the actions must not bubble up to it.
  const handleDownloadPdf = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDownloadPdf(contract)
  }

  const handleSendEmail = (event: React.MouseEvent) => {
    event.stopPropagation()
    onSendEmail(contract)
  }

  return (
    <Table.Tr style={{ cursor: 'pointer' }} onClick={handleClick}>
      <Table.Td fw={500}>{contract.customer_name}</Table.Td>
      <Table.Td>{summary?.orders_count ?? 0}</Table.Td>
      <Table.Td>{formatCurrency(summary?.total ?? 0)}</Table.Td>
      {showInvoices && (
        <Table.Td>
          <Group gap={4}>
            {invoices.map((invoice) => (
              <InvoiceStateBadge key={invoice.id} state={invoice.state} size="sm" />
            ))}
          </Group>
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="xs" justify="flex-end" wrap="nowrap">
          <Tooltip label="Télécharger PDF" withArrow>
            <ActionIcon
              variant="light"
              size={36}
              onClick={handleDownloadPdf}
              disabled={!hasOrders}
              aria-label="Télécharger PDF"
            >
              <IconDownload size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Envoyer par email" withArrow>
            <ActionIcon
              size={36}
              onClick={handleSendEmail}
              loading={isSendingEmail}
              disabled={!hasOrders}
              aria-label="Envoyer par email"
            >
              <IconSend size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  )
}

export default function RecapsIndex({
  contracts,
  summaries,
  month,
  invoices,
  e_invoicing_connected,
}: RecapsIndexPageProps) {
  const { current_organization } = usePage<SharedProps>().props

  const basePath = `/organizations/${current_organization?.slug}/recaps`

  const [searchQuery, setSearchQuery] = useState('')
  const { sendingContractId, missingEmailContract, closeMissingEmail, downloadPdf, sendEmail } = useRecapActions(
    current_organization?.slug,
    month,
  )

  const handleSelectContract = (contract: Contract) =>
    router.get(`${basePath}/${contract.id}`, { month }, { preserveState: true })

  const handleMonthChange = (value: string) => router.get(basePath, { month: value }, { preserveState: true })

  const handleClearSearch = () => setSearchQuery('')

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(event.currentTarget.value)

  const normalizedQuery = searchQuery.trim().toLowerCase()
  const filteredContracts = normalizedQuery
    ? contracts.filter((contract) =>
        [contract.customer_name, contract.customer_address, contract.customer_email]
          .some((field) => field?.toLowerCase().includes(normalizedQuery)),
      )
    : contracts

  return (
    <>
      <Head title={`Récapitulatif Sociétés - ${current_organization?.name}`} />

      <Stack gap="md">
        <Group justify="space-between">
          <Title order={3}>Récapitulatif mensuel par société</Title>
          {e_invoicing_connected && (
            <Button
              component={Link}
              href={`/organizations/${current_organization?.slug}/invoice_batches/new?month=${month}`}
              leftSection={<IconFileInvoice size={16} />}
            >
              Facturer le mois
            </Button>
          )}
        </Group>

        <Group gap="sm" justify="space-between">
          <MonthNavigation month={month} onChange={handleMonthChange} />
          <TextInput
            placeholder="Rechercher une société"
            leftSection={<IconSearch size={16} />}
            rightSection={
              searchQuery ? (
                <ActionIcon variant="subtle" color="gray" onClick={handleClearSearch} aria-label="Effacer la recherche">
                  <IconX size={16} />
                </ActionIcon>
              ) : null
            }
            value={searchQuery}
            onChange={handleSearchChange}
            w={300}
          />
        </Group>

        <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
          <Table.ScrollContainer minWidth={500}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Société</Table.Th>
                  <Table.Th w={140}>Commandes</Table.Th>
                  <Table.Th w={140}>Total HT</Table.Th>
                  {e_invoicing_connected && <Table.Th w={180}>Facture</Table.Th>}
                  <Table.Th w={110} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredContracts.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={e_invoicing_connected ? 5 : 4}>
                      <Text c="dimmed" ta="center" py="md">
                        {contracts.length === 0 ? 'Aucune société enregistrée' : 'Aucune société trouvée'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {filteredContracts.map((contract) => (
                  <ContractRow
                    key={contract.id}
                    contract={contract}
                    summary={summaries[contract.id]}
                    invoices={invoices[contract.id] ?? []}
                    showInvoices={e_invoicing_connected}
                    isSendingEmail={sendingContractId === contract.id}
                    onSelect={handleSelectContract}
                    onDownloadPdf={downloadPdf}
                    onSendEmail={sendEmail}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      </Stack>

      <MissingEmailModal contract={missingEmailContract} onClose={closeMissingEmail} />
    </>
  )
}
